// rm-player.js — universal player. Audio / AudioLyrics / HTML / AudioHTML / Video.
//
// Mirrors `Assets/_App/UI/Screens/PlayerScreen.cs` — one screen, branches on
// `item.type`. The branches:
//
//   Audio        → audio shell + transport (no lyrics).
//   AudioLyrics  → audio shell + transport + bilingual lyrics strip.
//   HTML         → sandboxed iframe pane; 5s-dwell heuristic for play count.
//   AudioHTML    → iframe pane + transport (no lyrics). Audio is the item's
//                  companion (resolved via `item.companionAudioId`). Progress
//                  is keyed against the AudioHTML item's id, NOT the
//                  companion's id — the user navigated to the AudioHTML
//                  item, so that's what gets a play counted.
//   Video        → unsupported placeholder until Brief 004.
//
// Architecture rule: subscribes to engine events via `addCleanup()` to avoid
// the event-handler-stacking pitfall called out in CLAUDE.md.

import { RMComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { services } from "../platform-services.js";
import { AudioEngine } from "../engines/audio-engine.js";
import * as progressManager from "../engines/progress-manager.js";
import * as content from "../engines/content-loader.js";
import * as entitlement from "../engines/entitlement-service.js";
import { resolveAudioSources, resolveHtml } from "../data/media-paths.js";
import { PLAY, PAUSE, STOP, REWIND } from "../ui/icons.js";

const PLAYHEAD_SAVE_INTERVAL_MS = 5000;
// Heuristic for HTML items: stay on the page this long → counts as a play.
// HTMLs have no duration, so the 0.85 threshold from
// progressManager.shouldCountPlay() doesn't apply.
const HTML_DWELL_TO_COUNT_MS = 5000;

export class RMPlayer extends RMComponent {
  static props = ["itemId"];
  /** @type {string | null} */
  itemId = null;

  static styles = `
    :host { display: block; padding: 1rem; max-width: 56rem; margin: 0 auto; }
    h2 { margin: 0 0 0.25rem; font-size: 1.25rem; }
    .desc { color: var(--rm-muted); font-size: 0.875rem; margin-bottom: 1rem; }
    .controls { display: flex; gap: 0.5rem; align-items: center; margin-bottom: 1rem; }
    button { border-radius: 0.5rem; border: 1px solid var(--rm-border); background: var(--rm-surface); color: var(--rm-fg); cursor: pointer; font: inherit; }
    button:hover { background: var(--rm-surface-hover); }
    button:disabled { opacity: 0.5; cursor: default; }
    /* Icon transport buttons: ≥40px hit area, glyph driven by font-size + currentColor. */
    .icon-btn {
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 2.5rem; min-height: 2.5rem; padding: 0.5rem;
      font-size: 1.25rem; line-height: 1;
    }
    .icon-btn.primary { font-size: 1.5rem; }
    .icon-btn svg { display: block; }
    button:focus-visible { outline: 2px solid var(--rm-accent, currentColor); outline-offset: 2px; }
    /* Visually-hidden live region — announces play state to screen readers. */
    .sr-only {
      position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
      overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
    }
    .error { color: var(--rm-error); font-size: 0.85rem; margin: 0.5rem 0; }
    rm-audio-bar { display: block; margin-bottom: 0.75rem; }
    rm-lyrics-display {
      display: block;
      background: var(--rm-surface);
      border-radius: 0.5rem;
      padding: 0.5rem;
      max-height: 50vh;
      overflow: auto;
    }
    rm-html-pane { display: block; margin-bottom: 1rem; }
  `;

  /** @type {AudioEngine | null} */ _engine = null;
  /** @type {import("../types").RMItem | null} */ _item = null;
  /** @type {import("../types").RMItem | null} */ _audioItem = null;

  // Cached element refs from the imperative shell.
  /** @type {HTMLButtonElement | null} */ _playBtn = null;
  /** @type {HTMLElement | null} */      _bar = null;
  /** @type {HTMLElement | null} */      _lyricsEl = null;
  /** @type {HTMLElement | null} */      _statusEl = null;
  /** @type {HTMLElement | null} */      _errorEl = null;

  /** @type {number} */ _lastPlayheadSavedMs = 0;
  /** @type {boolean} */ _playRecorded = false;
  /** @type {number} */ _lastSeenTime = 0;

  async onConnected() {
    if (!this.itemId) return;
    const item = await content.getItem(this.itemId);
    if (!item) {
      this.shadowRoot?.replaceChildren(html`<p>Item not found: ${this.itemId}</p>`);
      return;
    }
    this._item = item;

    // Entitlement gate (Brief 005a). Audio-bearing items (Audio / AudioLyrics
    // / AudioHTML) require an unlock; HTML-only items stay open (free browse).
    // While the service is pending it defaults to the free set, so this never
    // flashes a lockout on boot. Phase-1 is UI-only — the media path itself is
    // unchanged (signed URLs are 005b / addendum §6).
    const audioBearing =
      item.type === "Audio" || item.type === "AudioLyrics" || item.type === "AudioHTML";
    if (audioBearing && !entitlement.isUnlocked(item.id)) {
      const reason = entitlement.status() === "expired" ? "expired" : "locked";
      this._renderUpgrade(item, reason);
      return;
    }

    switch (item.type) {
      case "Audio":
      case "AudioLyrics":
        await this._renderAudio(item);
        break;
      case "HTML":
        this._renderHtml(item);
        break;
      case "AudioHTML":
        await this._renderAudioHtml(item);
        break;
      case "Video":
      default:
        this._renderUnsupported(item);
        break;
    }
  }

  // ── Audio + AudioLyrics ────────────────────────────────────────────────────

  /** @param {import("../types").RMItem} item */
  async _renderAudio(item) {
    this._audioItem = item;
    // Render the shell first so iOS Safari has a real Play button to satisfy
    // the user-gesture requirement before we await any I/O. The shell exposes
    // element refs (_playBtn, _bar, _lyricsEl, …) used by event handlers.
    this._renderAudioShell(item);

    // Lazy-load lyrics for AudioLyrics items. The hasLyrics flag was set at
    // index-load time from the kind=lyrics entries in content_index.json.
    /** @type {import("../types").LyricLine[]} */
    let lyrics = [];
    if (item.hasLyrics) lyrics = await content.loadLyrics(item);

    if (this._lyricsEl) {
      /** @type {any} */ (this._lyricsEl).lines = lyrics;
      /** @type {any} */ (this._lyricsEl).activeIndex = -1;
    }

    await this._wireEngineFor(item, item, lyrics);
  }

  // ── HTML ───────────────────────────────────────────────────────────────────

  /** @param {import("../types").RMItem} item */
  _renderHtml(item) {
    const root = this.shadowRoot;
    if (!root) return;
    const url = resolveHtml(item.mediaFilename);
    root.replaceChildren(html`
      <style>${RMPlayer.styles}</style>
      <h2>${item.title}</h2>
      <div class="desc">${item.description}</div>
      <p class="error" id="error"></p>
      <rm-html-pane id="pane"></rm-html-pane>
    `);
    this._errorEl = /** @type {HTMLElement | null} */ (root.getElementById("error"));
    // Set the prop imperatively — `html` tagged-template attributes are
    // lowercased by the HTML parser, so `.htmlUrl=` would become `.htmlurl=`
    // (lowercase property name, not what the component reads).
    const pane = /** @type {any} */ (root.getElementById("pane"));
    if (pane) pane.htmlUrl = url;

    // 5-second dwell counts as a play. Cancel if the screen tears down first.
    const timer = window.setTimeout(() => {
      if (!this._item || this._playRecorded) return;
      this._playRecorded = true;
      void progressManager.recordPlay(this._item.id);
    }, HTML_DWELL_TO_COUNT_MS);
    this.addCleanup(() => clearTimeout(timer));
  }

  // ── AudioHTML ──────────────────────────────────────────────────────────────

  /** @param {import("../types").RMItem} item */
  async _renderAudioHtml(item) {
    const url = resolveHtml(item.mediaFilename);
    this._renderAudioHtmlShell(item, url);

    // Resolve the companion audio. Defensive: if it's missing, render the
    // HTML alone with a clear note in the error area.
    const companion = item.companionAudioId
      ? await content.getItem(item.companionAudioId)
      : null;
    if (!companion) {
      console.warn(`[rm-player] AudioHTML ${item.id}: companion ${item.companionAudioId} not found`);
      if (this._errorEl) {
        this._errorEl.textContent = `Companion audio "${item.companionAudioId}" not found — HTML only.`;
      }
      if (this._playBtn) this._playBtn.disabled = true;
      return;
    }
    this._audioItem = companion;

    // No lyrics for AudioHTML — the data model says these items don't ship
    // lyric tracks, and the HTML page IS the visual.
    await this._wireEngineFor(item, companion, []);
  }

  // ── Engine + transport binding (shared by AudioLyrics + AudioHTML) ────────

  /**
   * Build an AudioEngine that drives `audioItem`'s media, but reports
   * progress against `progressItem.id`. For AudioLyrics, both are the same;
   * for AudioHTML, `progressItem` is the AudioHTML item (the navigation
   * target) and `audioItem` is its resolved companion.
   *
   * @param {import("../types").RMItem} progressItem
   * @param {import("../types").RMItem} audioItem
   * @param {import("../types").LyricLine[]} lyrics
   */
  async _wireEngineFor(progressItem, audioItem, lyrics) {
    const engine = new AudioEngine(services.audioPlayer);
    this._engine = engine;
    engine.load(audioItem, resolveAudioSources(audioItem.mediaFilename), lyrics);

    // Restore the saved playhead (don't auto-play — iOS Safari will reject).
    const resumeAt = await progressManager.getPlayhead(progressItem.id);
    if (resumeAt > 0 && resumeAt < (audioItem.durationSec || Infinity) - 1) {
      engine.seekTo(resumeAt);
      this._lastSeenTime = resumeAt;
    }

    // ── Engine event subscriptions ─────────────────────────────────────────
    // CLAUDE.md "Event handler stacking": every addEventListener pairs with
    // an addCleanup so re-mounting this screen doesn't double-fire handlers.

    /** @type {EventListener} */
    const onState = (e) => {
      const next = /** @type {CustomEvent<string>} */ (e).detail;
      this._updatePlayButton(next);
    };
    engine.addEventListener("stateChanged", onState);
    this.addCleanup(() => engine.removeEventListener("stateChanged", onState));

    /** @type {EventListener} */
    const onTime = (e) => {
      const t = /** @type {CustomEvent<number>} */ (e).detail;
      this._lastSeenTime = t;
      if (this._bar) /** @type {any} */ (this._bar).currentTimeSec = t;
      this._maybeSavePlayhead(t);
      this._maybeRecordPlayMidstream(t);
    };
    engine.addEventListener("timeUpdated", onTime);
    this.addCleanup(() => engine.removeEventListener("timeUpdated", onTime));

    // Lyric line updates only meaningful for AudioLyrics; for Audio/AudioHTML
    // there's no lyrics-display element to update. Keep the subscription
    // unconditional — it's a no-op when _lyricsEl is null.
    /** @type {EventListener} */
    const onLine = (e) => {
      const idx = /** @type {CustomEvent<number>} */ (e).detail;
      if (this._lyricsEl) /** @type {any} */ (this._lyricsEl).activeIndex = idx;
      this._scrollLyricIntoView(idx);
    };
    engine.addEventListener("lyricLineChanged", onLine);
    this.addCleanup(() => engine.removeEventListener("lyricLineChanged", onLine));

    /** @type {EventListener} */
    const onEnded = () => { void this._handlePlaybackEnded(); };
    engine.addEventListener("playbackEnded", onEnded);
    this.addCleanup(() => engine.removeEventListener("playbackEnded", onEnded));

    /** @type {EventListener} */
    const onError = (e) => {
      const msg = String(/** @type {CustomEvent<string>} */ (e).detail || "");
      if (this._errorEl) this._errorEl.textContent = msg;
    };
    engine.addEventListener("error", onError);
    this.addCleanup(() => engine.removeEventListener("error", onError));

    // Bar emits {detail: timeSec} on user scrub.
    /** @type {EventListener} */
    const onSeek = (e) => {
      const t = /** @type {CustomEvent<number>} */ (e).detail;
      engine.seekTo(t);
    };
    if (this._bar) {
      this._bar.addEventListener("seek", onSeek);
      this.addCleanup(() => this._bar?.removeEventListener("seek", onSeek));
    }

    // Initial UI sync now that everything is wired.
    this._updatePlayButton("Ready");
    if (this._bar) {
      /** @type {any} */ (this._bar).currentTimeSec = this._lastSeenTime;
      /** @type {any} */ (this._bar).durationSec = audioItem.durationSec;
    }

    this.addCleanup(() => {
      // Flush playhead on screen teardown — pause / nav-away / reload.
      if (this._engine && this._item) {
        const t = this._engine.snapshot().currentTimeSec;
        if (t > 0) void progressManager.savePlayhead(this._item.id, t);
      }
      this._engine?.dispose();
      this._engine = null;
    });
  }

  // ── Shells ─────────────────────────────────────────────────────────────────

  /** @param {import("../types").RMItem} item */
  _renderAudioShell(item) {
    const root = this.shadowRoot;
    if (!root) return;
    const frag = html`
      <style>${RMPlayer.styles}</style>
      <h2>${item.title}</h2>
      <div class="desc">${item.description}</div>
      <rm-audio-bar id="bar"></rm-audio-bar>
      ${this._buildTransport()}
      <p class="error" id="error"></p>
      <rm-lyrics-display id="lyrics"></rm-lyrics-display>
    `;
    root.replaceChildren(frag);
    this._cacheTransportRefs(root);
    this._lyricsEl = /** @type {HTMLElement | null} */ (root.getElementById("lyrics"));
  }

  /**
   * AudioHTML shell: HTML pane on top, transport below, no lyrics strip.
   * @param {import("../types").RMItem} item
   * @param {string} htmlUrl
   */
  _renderAudioHtmlShell(item, htmlUrl) {
    const root = this.shadowRoot;
    if (!root) return;
    const frag = html`
      <style>${RMPlayer.styles}</style>
      <h2>${item.title}</h2>
      <div class="desc">${item.description}</div>
      <rm-html-pane id="pane"></rm-html-pane>
      <rm-audio-bar id="bar"></rm-audio-bar>
      ${this._buildTransport()}
      <p class="error" id="error"></p>
    `;
    root.replaceChildren(frag);
    this._cacheTransportRefs(root);
    // See _renderHtml for why this is set imperatively.
    const pane = /** @type {any} */ (root.getElementById("pane"));
    if (pane) pane.htmlUrl = htmlUrl;
    this._lyricsEl = null;
  }

  /**
   * Build the icon transport (rewind / play-pause / stop) as a real DOM node,
   * shared verbatim by both audio shells. Caches `_playBtn` and `_statusEl`
   * and wires the button clicks (paired with addCleanup per the event-stacking
   * pitfall). Returns the `.controls` element for interpolation.
   * @returns {HTMLElement}
   */
  _buildTransport() {
    const wrap = document.createElement("div");
    wrap.className = "controls";

    const rewind = this._iconButton(REWIND, "Rewind 10 seconds");
    const onRewind = () => this._engine?.seekBack10s();
    rewind.addEventListener("click", onRewind);
    this.addCleanup(() => rewind.removeEventListener("click", onRewind));

    const play = this._iconButton(PLAY, "Play");
    play.id = "play";
    play.classList.add("primary");
    const onPlay = () => this._togglePlay();
    play.addEventListener("click", onPlay);
    this.addCleanup(() => play.removeEventListener("click", onPlay));

    const stop = this._iconButton(STOP, "Stop");
    stop.id = "stop";
    const onStop = () => this._stop();
    stop.addEventListener("click", onStop);
    this.addCleanup(() => stop.removeEventListener("click", onStop));

    const status = document.createElement("span");
    status.id = "status";
    status.className = "sr-only";
    status.setAttribute("aria-live", "polite");

    wrap.append(rewind, play, stop, status);
    this._playBtn = play;
    this._statusEl = status;
    return wrap;
  }

  /**
   * A bare icon button. The SVG constant is static (no user input), so
   * `innerHTML` is safe. The button's `aria-label` carries the accessible
   * name; the glyph itself is `aria-hidden`.
   * @param {string} svg @param {string} label @returns {HTMLButtonElement}
   */
  _iconButton(svg, label) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "icon-btn";
    b.setAttribute("aria-label", label);
    b.innerHTML = svg;
    return b;
  }

  /** @param {ShadowRoot} root */
  _cacheTransportRefs(root) {
    this._bar = /** @type {HTMLElement | null} */ (root.getElementById("bar"));
    this._errorEl = /** @type {HTMLElement | null} */ (root.getElementById("error"));
  }

  /**
   * Locked audio item → render the upsell in place of the transport. The
   * `<rm-upgrade-card>` owns its own focus/keyboard handling and dismiss;
   * this screen just mounts it with the item context. No engine is created,
   * so there's nothing extra to tear down here.
   * @param {import("../types").RMItem} item
   * @param {"locked"|"expired"} reason
   */
  _renderUpgrade(item, reason) {
    const root = this.shadowRoot;
    if (!root) return;
    root.replaceChildren(html`
      <style>${RMPlayer.styles}</style>
      <h2>${item.title}</h2>
      <div class="desc">${item.description}</div>
      <rm-upgrade-card id="upsell"></rm-upgrade-card>
    `);
    const card = /** @type {any} */ (root.getElementById("upsell"));
    if (card) {
      card.itemId = item.id;
      card.reason = reason;
    }
  }

  /** @param {import("../types").RMItem} item */
  _renderUnsupported(item) {
    const root = this.shadowRoot;
    if (!root) return;
    /** @type {Record<string, string>} */
    const briefByType = { Video: "Brief 004" };
    const which = briefByType[item.type] || "a future brief";
    root.replaceChildren(html`
      <style>${RMPlayer.styles}</style>
      <h2>${item.title}</h2>
      <div class="desc">${item.description}</div>
      <p class="desc">
        Item type <code>${item.type}</code> is not yet implemented in this build.
        Support arrives in ${which}.
      </p>
    `);
  }

  // ── Transport helpers ──────────────────────────────────────────────────────

  _togglePlay() {
    const e = this._engine;
    if (!e) return;
    const s = e.snapshot().state;
    if (s === "Playing") e.pause();
    else void e.play();
  }

  /**
   * Stop: pause and rewind to 0 so the next Play starts from the top. The
   * engine's stop() already does pause + seek0 (state → "Ready"), which the
   * stateChanged handler turns into the play glyph. The time bar isn't driven
   * by a timeupdate while paused, so reset it here.
   */
  _stop() {
    const e = this._engine;
    if (!e) return;
    e.stop();
    this._lastSeenTime = 0;
    if (this._bar) /** @type {any} */ (this._bar).currentTimeSec = 0;
  }

  /** @param {string} state */
  _updatePlayButton(state) {
    if (!this._playBtn) return;
    const playing = state === "Playing";
    this._playBtn.innerHTML = playing ? PAUSE : PLAY;
    this._playBtn.setAttribute("aria-label", playing ? "Pause" : "Play");
    this._playBtn.disabled = state === "Idle" || state === "Error";
    if (this._statusEl) this._statusEl.textContent = state;
  }

  /** @param {number} idx */
  _scrollLyricIntoView(idx) {
    if (!this._lyricsEl || idx < 0) return;
    const inner = this._lyricsEl.shadowRoot?.querySelector(`[data-i="${idx}"]`);
    if (inner instanceof HTMLElement) {
      inner.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  /** @param {number} t */
  _maybeSavePlayhead(t) {
    if (!this._item) return;
    const now = Date.now();
    if (now - this._lastPlayheadSavedMs < PLAYHEAD_SAVE_INTERVAL_MS) return;
    this._lastPlayheadSavedMs = now;
    void progressManager.savePlayhead(this._item.id, t);
  }

  /**
   * Mirrors ProgressManager.ShouldCountPlay: count a play once the user has
   * crossed the 0.85 threshold mid-stream, even if they never hear "ended"
   * (e.g. they drag the scrubber past the end).
   *
   * Note: progress is keyed against `_item.id`, but the duration we compare
   * against is `_audioItem.durationSec` — for AudioHTML these differ
   * (AudioHTML items have durationSec=0; the companion has the real
   * duration).
   * @param {number} t
   */
  _maybeRecordPlayMidstream(t) {
    if (this._playRecorded || !this._item || !this._audioItem) return;
    const dur = this._audioItem.durationSec;
    if (!progressManager.shouldCountPlay(t, dur)) return;
    this._playRecorded = true;
    void progressManager.recordPlay(this._item.id);
  }

  async _handlePlaybackEnded() {
    if (!this._item) return;
    if (!this._playRecorded) {
      this._playRecorded = true;
      await progressManager.recordPlay(this._item.id);
    }
    // Reset playhead so next mount starts from the top.
    await progressManager.savePlayhead(this._item.id, 0);
    if (this._statusEl) this._statusEl.textContent = "Played";
  }

  render() { /* imperative render in onConnected */ }
}
